import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/authAPI';
import { validateEmail, validatePhone, validateName, formatAPIError } from '../utils/validation';
import Header from '../components/layout/Header';
import Sections from "./Sections"; 
import './styles/Profile.css';
import ProfileReferencesTab from '../components/profileReference/ProfileReferencesTab';

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateUser, token } = useAuth();

  const role = user?.role;
  const canEditOnlyPersonal = role === "EMPLOYER" || role === "AGENCY";

  const DEV_TABS = useMemo(() => {
    return canEditOnlyPersonal
      ? ["Personal"]
      : ["Personal", "Professional", "References", "Summary"];
  }, [canEditOnlyPersonal]);

  const [activeStep, setActiveStep] = useState("personal");

  // keep employer/agency locked on Personal
  useEffect(() => {
    if (canEditOnlyPersonal && activeStep !== "personal") {
      setActiveStep("personal");
    }
  }, [canEditOnlyPersonal, activeStep]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    headline: '',
    email: '',
    countryCode: '+1',
    phone: '',
    fullPhone: '',
    city: '',
    province: '',
    country: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');

  // Track if email/phone were provided during registration
  const [canEditEmail, setCanEditEmail] = useState(false);
  const [canEditPhone, setCanEditPhone] = useState(false);

  const countryCodes = useMemo(
    () => [
      { code: '+1', name: 'US/CA' },
      { code: '+44', name: 'UK' },
      { code: '+91', name: 'India' },
      { code: '+86', name: 'China' },
      { code: '+33', name: 'France' },
      { code: '+49', name: 'Germany' },
      { code: '+81', name: 'Japan' },
      { code: '+61', name: 'Australia' },
      { code: '+39', name: 'Italy' },
      { code: '+34', name: 'Spain' },
      { code: '+7', name: 'Russia' },
      { code: '+55', name: 'Brazil' },
      { code: '+52', name: 'Mexico' },
      { code: '+82', name: 'S. Korea' },
      { code: '+65', name: 'Singapore' },
      { code: '+971', name: 'UAE' }
    ],
    []
  );

  const parsePhoneNumber = useCallback((fullPhone) => {
  if (!fullPhone) return { countryCode: '+1', phone: '' };

  for (const c of countryCodes) {
    if (fullPhone.startsWith(c.code)) {
      return { countryCode: c.code, phone: fullPhone.substring(c.code.length) };
    }
  }

  if (fullPhone.startsWith('+')) {
    return { countryCode: '+1', phone: fullPhone };
  }

  return { countryCode: '+1', phone: fullPhone };
}, [countryCodes]);


  // Update fullPhone when countryCode or phone changes
  useEffect(() => {
   // If user is typing a +country code directly, do not overwrite it
  if (formData.fullPhone?.startsWith('+')) return;

  const fullPhone = formData.phone
    ? `${formData.countryCode}${formData.phone}`
    : formData.countryCode;

  if (fullPhone !== formData.fullPhone) {
    setFormData((prev) => ({ ...prev, fullPhone }));
  }
}, [formData.countryCode, formData.phone, formData.fullPhone]);



  // Load existing profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await authAPI.getProfile();
        const profileData = response.data;

        const email = profileData.account?.email || user?.email || '';
        const fullPhone = profileData.account?.phone || user?.phone || '';
        const { countryCode, phone } = parsePhoneNumber(fullPhone);

        setCanEditEmail(!email || email.trim() === '');
        setCanEditPhone(!fullPhone || fullPhone.trim() === '');

        setFormData({
          firstName: profileData.firstName || '',
          lastName: profileData.lastName || '',
          dob: profileData.dob ? new Date(profileData.dob).toISOString().split('T')[0] : '',
          headline: profileData.headline || '',
          email,
          countryCode,
          phone,
          fullPhone,
          city: profileData.city || '',
          province: profileData.province || '',
          country: profileData.country || ''
        });
      } catch (error) {
        const userEmail = user?.email || '';
        const userFullPhone = user?.phone || '';
        const { countryCode, phone } = parsePhoneNumber(userFullPhone);

        setCanEditEmail(!userEmail || userEmail.trim() === '');
        setCanEditPhone(!userFullPhone || userFullPhone.trim() === '');

        setFormData((prev) => ({
          ...prev,
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: userEmail,
          countryCode,
          phone,
          fullPhone: userFullPhone
        }));
      } finally {
        setLoadingProfile(false);
      }
    };

    if (token) loadProfile();
}, [token, user, parsePhoneNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name] || errors.submit) {
      setErrors((prev) => ({ ...prev, [name]: '', submit: '' }));
    }
    if (success) setSuccess('');
  };

  // Single UI input for contact number, internal split kept.
  const handleContactNumberChange = (e) => {
  const raw = e.target.value;

  if (raw.startsWith('+')) {
    const parsed = parsePhoneNumber(raw);

    setFormData((prev) => ({
      ...prev,
      fullPhone: raw,              // keep what user typed visible
      countryCode: parsed.countryCode,
      phone: parsed.phone          // will be "" for "+91" which is fine now
    }));
  } else {
    // if user types digits only, keep existing countryCode and rebuild
    setFormData((prev) => ({
      ...prev,
      phone: raw,
      fullPhone: `${prev.countryCode}${raw}`
    }));
  }

  if (errors.phone || errors.submit) {
    setErrors((prev) => ({ ...prev, phone: '', submit: '' }));
  }
  if (success) setSuccess('');
};



  const validateForm = () => {
    const newErrors = {};

    const firstNameError = validateName(formData.firstName);
    if (firstNameError) newErrors.firstName = firstNameError;

    const lastNameError = validateName(formData.lastName);
    if (lastNameError) newErrors.lastName = lastNameError;

    if (!formData.dob) {
      newErrors.dob = 'Date of Birth is required';
    } else {
      const dobDate = new Date(formData.dob);
      const today = new Date();
      const age = today.getFullYear() - dobDate.getFullYear();

      if (dobDate > today) newErrors.dob = 'Date of Birth cannot be in the future';
      else if (age < 16) newErrors.dob = 'You must be at least 16 years old';
      else if (age > 120) newErrors.dob = 'Please enter a valid date of birth';
    }

    if (canEditEmail && !formData.email) {
      newErrors.email = 'Email is required';
    } else if (formData.email) {
      const emailError = validateEmail(formData.email);
      if (emailError) newErrors.email = emailError;
    }

    if (formData.phone && formData.phone.trim() !== '') {
      const phoneError = validatePhone(formData.fullPhone);
      if (phoneError) newErrors.phone = phoneError;
    }

    if (formData.city && formData.city.length < 2) newErrors.city = 'City must be at least 2 characters';
    if (formData.province && formData.province.length < 2)
      newErrors.province = 'Province/State must be at least 2 characters';
    if (formData.country && formData.country.length < 2) newErrors.country = 'Country must be at least 2 characters';

    if (formData.headline && formData.headline.length > 100)
      newErrors.headline = 'Headline must be less than 100 characters';

    return newErrors;
  };

  const submitPersonal = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }

    setLoading(true);
    setErrors({});
    setSuccess('');

    try {
      const profilePayload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dob: formData.dob,
        headline: formData.headline.trim(),
        city: formData.city.trim(),
        province: formData.province.trim(),
        country: formData.country.trim()
      };

      if (canEditEmail && formData.email) profilePayload.email = formData.email.trim();
      if (canEditPhone && formData.phone && formData.phone.trim() !== '') profilePayload.phone = formData.fullPhone;

      await authAPI.updateProfile(profilePayload);

      updateUser({
        ...user,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || user?.email,
        phone: formData.fullPhone || user?.phone,
        city: formData.city || user?.city,
        country: formData.country || user?.country
      });

      setSuccess('✅ Profile updated successfully!');

      if (canEditEmail && formData.email) setCanEditEmail(false);
      if (canEditPhone && formData.phone) setCanEditPhone(false);

      return true;
    } catch (error) {
      setErrors({ submit: formatAPIError(error) });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
  // Employer/Agency: just save personal and stay on same page
  if (canEditOnlyPersonal) {
    await submitPersonal();
    return;
  }

  // Candidate flow (existing)
  if (activeStep === "personal") {
    const ok = await submitPersonal();
    if (!ok) return;
    setActiveStep("professional");
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  if (activeStep === "professional") {
    setActiveStep("references");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};


  const handleBack = () => {
    if (activeStep === 'references') setActiveStep('professional');
    else if (activeStep === 'professional') setActiveStep('personal');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderStepContent = () => {
    if (activeStep === 'personal') {
      return (
        <div className="cps-card">
          <div className="cps-card-header">
            <div className="cps-card-title">Personal</div>
            <div className="cps-card-subtitle">Please provide your basic personal information</div>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="cps-form">
            <div className="cps-grid-2">
              <div className="cps-field">
                <label className="cps-label">
                  First Name
                </label>
                <input
                  className={`cps-input ${errors.firstName ? 'error' : ''}`}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                />
                {errors.firstName && <div className="cps-error">{errors.firstName}</div>}
              </div>

              <div className="cps-field">
                <label className="cps-label">
                  Last Name
                </label>
                <input
                  className={`cps-input ${errors.lastName ? 'error' : ''}`}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Doe"
                />
                {errors.lastName && <div className="cps-error">{errors.lastName}</div>}
              </div>
            </div>

            <div className="cps-grid-2">
              <div className="cps-field">
                <label className="cps-label">
                  Date of Birth
                </label>
                <div className="cps-input-icon-wrap">
                  <input
                    type="date"
                    className={`cps-input cps-input-icon ${errors.dob ? 'error' : ''}`}
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    placeholder="dd-mm-yyyy"
                  />
                
                </div>
                {errors.dob && <div className="cps-error">{errors.dob}</div>}
              </div>

              <div className="cps-field">
                <label className="cps-label">
                  Contact Number
                </label>
                <input
  className={`cps-input ${errors.phone ? 'error' : ''}`}
  value={formData.fullPhone || ''}
  onChange={handleContactNumberChange}
  placeholder="+1 234 567 8900"
  disabled={!canEditPhone && Boolean((user?.phone || formData.fullPhone || '').trim())}
/>

                {errors.phone && <div className="cps-error">{errors.phone}</div>}
              </div>
            </div>

            <div className="cps-field">
              <label className="cps-label">
                Email Address
              </label>
              <input
                className={`cps-input ${errors.email ? 'error' : ''}`}
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john.doe@example.com"
                disabled={!canEditEmail && Boolean((user?.email || formData.email || '').trim())}
              />
              {errors.email && <div className="cps-error">{errors.email}</div>}
            </div>

            <div className="cps-section-title">Location</div>

            <div className="cps-grid-3">
              <div className="cps-field">
                <label className="cps-label">
                  City
                </label>
                <input
                  className={`cps-input ${errors.city ? 'error' : ''}`}
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="New York"
                />
                {errors.city && <div className="cps-error">{errors.city}</div>}
              </div>

              <div className="cps-field">
                <label className="cps-label">
                  Province/State
                </label>
                <input
                  className={`cps-input ${errors.province ? 'error' : ''}`}
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  placeholder="NY"
                />
                {errors.province && <div className="cps-error">{errors.province}</div>}
              </div>

              <div className="cps-field">
                <label className="cps-label">
                  Country
                </label>
                <input
                  className={`cps-input ${errors.country ? 'error' : ''}`}
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="USA"
                />
                {errors.country && <div className="cps-error">{errors.country}</div>}
              </div>
            </div>

            <div className="cps-field">
              <label className="cps-label">
                Headline / Professional Title
              </label>
              <input
                className={`cps-input ${errors.headline ? 'error' : ''}`}
                name="headline"
                value={formData.headline}
                onChange={handleChange}
                placeholder="Senior Software Engineer"
              />
              {errors.headline && <div className="cps-error">{errors.headline}</div>}
            </div>

            {errors.submit && <div className="cps-submit-error">{errors.submit}</div>}
            {success && <div className="cps-submit-success">{success}</div>}

            <div className="cps-actions">
              <button type="button" className="cps-next" onClick={handleNext} disabled={loading}>
  {loading ? "Saving..." : (canEditOnlyPersonal ? "Save" : "Next Step")}
</button>

            </div>
          </form>
        </div>
      );
    }

    if (activeStep === 'professional') {
      return (
        <div className="cps-card cps-empty">
          <div className="cps-card-header">
            <div className="cps-card-title">Professional</div>
            <div className="cps-card-subtitle">
    Add your education, certifications, and employment history
  </div>
          </div>

          <Sections />

          <div className="cps-actions cps-actions-between">
            <button type="button" className="cps-back" onClick={handleBack}>
              Back
            </button>
            <button type="button" className="cps-next" onClick={handleNext}>
              Next Step
            </button>
          </div>
        </div>
      );
    }
    
    if (activeStep === 'references') {
    return (
      <div className="cps-card cps-empty">
        <div className="cps-card-header">
          <div className="cps-card-title">References</div>
          <div className="cps-card-subtitle">
            <button
  type="button"
  className="cps-next"
  onClick={() => navigate('/candidate/references')}
>
  Open Reference System
</button>
<ProfileReferencesTab />
 </div>
        </div>

        <div className="cps-empty-body" />

        <div className="cps-actions cps-actions-between">
          <button type="button" className="cps-back" onClick={handleBack}>
            Back
          </button>
          <button type="button" className="cps-next" disabled>
            Next Step
          </button>
        </div>
      </div>
    );
  };
  };

  if (loadingProfile) {
    return (
      <>
        <Header />
        <div className="cps-page">
          <div className="cps-container">
            <div className="cps-loading">Loading...</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="cps-page">
        <div className="cps-topbar">
            <button
              type="button"
              className="cps-backlink"
              onClick={() => window.history.back()} // OR navigate('/dashboard')
            >
              ← Back to Dashboard
            </button>
          </div>
        <div className="cps-container">
          
          <div className="cps-dev-nav">
           
            <div className="cps-dev-tabs">
              {DEV_TABS.map((t) => {
                const isActiveTab =
                  (t === 'Personal' && activeStep === 'personal') ||
                  (t === 'Professional' && activeStep === 'professional') ||
                  (t === 'References' && activeStep === 'references');

                return (
                  <button
                    key={t}
                    type="button"
                    className={`cps-dev-tab ${isActiveTab ? 'active' : ''}`}
                    onClick={() => {
                      if (t === 'Personal') setActiveStep('personal');
                      if (t === 'Professional') setActiveStep('professional');
                      if (t === 'References') setActiveStep('references');
                    }}
                  >
                    {t}
                  </button>
                );
              })}
            </div>

          </div>

          {renderStepContent()}
        </div>
      </div>
    </>
  );
};

export default Profile;
